import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IconDownload,
    IconHistory,
    IconEye,
    IconPlus,
    IconPencil,
    IconTrash,
    IconMessage,
    IconReceipt,
    IconCircleCheck,
    IconMinus,
} from '@tabler/icons-react';
import { Button, Table, Text } from '@fragments';
import { getIntlLocale } from '@i18n';
import './styles.scss';

// API log type → label + icon. The keys on the left are API enums; the labels
// come from the caller's `t` so they follow the active language.
const getLogTypeMap = (t) => ({
    'community.created':       { label: t('chargebacks.logs.types.communityCreated'),        icon: <IconPlus size={13} /> },
    'community.edited':        { label: t('chargebacks.logs.types.communityEdited'),         icon: <IconPencil size={13} /> },
    'community.deleted':       { label: t('chargebacks.logs.types.communityDeleted'),        icon: <IconTrash size={13} /> },
    'content.created':         { label: t('chargebacks.logs.types.contentCreated'),          icon: <IconPlus size={13} /> },
    'content.updated':         { label: t('chargebacks.logs.types.contentUpdated'),          icon: <IconPencil size={13} /> },
    'content.deleted':         { label: t('chargebacks.logs.types.contentDeleted'),          icon: <IconTrash size={13} /> },
    'content.viewed':          { label: t('chargebacks.logs.types.contentViewed'),           icon: <IconEye size={13} /> },
    'content_comment.created': { label: t('chargebacks.logs.types.contentCommentCreated'),   icon: <IconMessage size={13} /> },
    'content_comment.updated': { label: t('chargebacks.logs.types.contentCommentUpdated'),   icon: <IconPencil size={13} /> },
    'content_comment.deleted': { label: t('chargebacks.logs.types.contentCommentDeleted'),   icon: <IconTrash size={13} /> },
    'playlist.created':        { label: t('chargebacks.logs.types.playlistCreated'),         icon: <IconPlus size={13} /> },
    'playlist.updated':        { label: t('chargebacks.logs.types.playlistUpdated'),         icon: <IconPencil size={13} /> },
    'playlist.deleted':        { label: t('chargebacks.logs.types.playlistDeleted'),         icon: <IconTrash size={13} /> },
    'section.created':         { label: t('chargebacks.logs.types.sectionCreated'),          icon: <IconPlus size={13} /> },
    'section.updated':         { label: t('chargebacks.logs.types.sectionUpdated'),          icon: <IconPencil size={13} /> },
    'section.deleted':         { label: t('chargebacks.logs.types.sectionDeleted'),          icon: <IconTrash size={13} /> },
    'subscription.created':    { label: t('chargebacks.logs.types.subscriptionCreated'),     icon: <IconReceipt size={13} /> },
    'subscription.paid':       { label: t('chargebacks.logs.types.subscriptionPaid'),        icon: <IconCircleCheck size={13} /> },
    'subscription.canceled':   { label: t('chargebacks.logs.types.subscriptionCanceled'),    icon: <IconTrash size={13} /> },
    'withdrawal.requested':    { label: t('chargebacks.logs.types.withdrawalRequested'),     icon: <IconReceipt size={13} /> },
    'withdrawal.configured':   { label: t('chargebacks.logs.types.withdrawalConfigured'),    icon: <IconPencil size={13} /> },
    'charge.refunded':         { label: t('chargebacks.logs.types.chargeRefunded'),          icon: <IconPencil size={13} /> },
    'charge.paid':             { label: t('chargebacks.logs.types.chargePaid'),              icon: <IconCircleCheck size={13} /> },
    'group.created':           { label: t('chargebacks.logs.types.groupCreated'),            icon: <IconPlus size={13} /> },
    'group.updated':           { label: t('chargebacks.logs.types.groupUpdated'),            icon: <IconPencil size={13} /> },
    'group.deleted':           { label: t('chargebacks.logs.types.groupDeleted'),            icon: <IconTrash size={13} /> },
    'post.created':            { label: t('chargebacks.logs.types.postCreated'),             icon: <IconPlus size={13} /> },
    'post.updated':            { label: t('chargebacks.logs.types.postUpdated'),             icon: <IconPencil size={13} /> },
    'post.deleted':            { label: t('chargebacks.logs.types.postDeleted'),             icon: <IconTrash size={13} /> },
    'material.created':        { label: t('chargebacks.logs.types.materialCreated'),         icon: <IconPlus size={13} /> },
    'material.updated':        { label: t('chargebacks.logs.types.materialUpdated'),         icon: <IconPencil size={13} /> },
    'material.deleted':        { label: t('chargebacks.logs.types.materialDeleted'),         icon: <IconTrash size={13} /> },
    'material.downloaded':     { label: t('chargebacks.logs.types.materialDownloaded'),      icon: <IconDownload size={13} /> },
    'material.viewed':         { label: t('chargebacks.logs.types.materialViewed'),          icon: <IconEye size={13} /> },
});

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(getIntlLocale(), {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

const getColumns = (t, logTypeMap) => [
    {
        id: 'type',
        title: t('chargebacks.logs.action'),
        minWidth: 180,
        customRender: (item) => {
            const { label, icon } = logTypeMap[item.type] ?? { label: item.type, icon: <IconMinus size={13} /> };
            return (
                <span className="log-type-badge">
                    {icon}
                    {label}
                </span>
            );
        },
    },
    {
        id: 'details',
        title: t('chargebacks.logs.details'),
        grow: 1,
        customRender: (item) => {
            const { label, material_id, ...rest } = item.details ?? {};
            const subtitle = material_id ?? Object.values(rest)[0] ?? null;
            return (
                <div className="log-detail-label">
                    {label
                        ? <Text.span contrast={7}>{label}</Text.span>
                        : <Text.span contrast={4}>—</Text.span>
                    }
                    {subtitle && <span className="log-detail-id">{subtitle}</span>}
                </div>
            );
        },
    },
    {
        id: 'created',
        title: t('common:fields.date'),
        minWidth: 160,
        render: (item) => formatDate(item.timestamps?.created),
    },
];

export default function ChargebackLogsView({ username, logs, loading, pagination, fetchLogs, exportLogs }) {
    const { t } = useTranslation('banking');

    // `t` changes identity on a language switch, so the labels re-resolve.
    const logTypeMap = useMemo(() => getLogTypeMap(t), [t]);
    const columns = useMemo(() => getColumns(t, logTypeMap), [t, logTypeMap]);

    return (
        <div className="chargeback-logs mt-5">
            <div className="chargeback-logs-header">
                <Text.h3 left={<IconHistory size={22} />}>
                    {t('chargebacks.logs.title')}
                </Text.h3>
                <Button
                    variant="dashed"
                    size="small"
                    startIcon={<IconDownload size={15} />}
                    text={t('chargebacks.logs.export')}
                    disabled={logs?.length === 0}
                    onClick={exportLogs}
                />
            </div>

            {username && (
                <Text.span contrast={5} className="mb-2" style={{ display: 'block' }}>
                    {t('chargebacks.logs.description', { name: username.split(' ')[0] })}
                </Text.span>
            )}

            <Table
                id="chargeback-user-logs-table"
                columns={columns}
                data={logs}
                loading={loading}
                loadingRows={6}
                contained
                enablePagination
                pages={pagination.pages}
                records={pagination.records}
                page={pagination.page}
                onSelectPage={fetchLogs}
                emptyMessage={t('chargebacks.logs.empty')}
                emptySubtitle={t('chargebacks.logs.emptySubtitle')}
            />
        </div>
    );
}
